import type { Action } from './actions'
import type { MetaProduct, MapById, MapListById } from './types'
import type {
    CacAllergen,
    CacCatalog,
    CacCategory,
    CacProduct,
    CacProductCategory,
    CacProductGroup,
    CacMenu,
    CacIngredient,
    CacPromotion,
    CacGame,
    OperationDevice
} from 'shared/types'

export type State = {
    catalog?: CacCatalog,
    selectedAllergens: ?Array<string>,
    withoutBacon: boolean,
    allergensById?: MapById<CacAllergen>,
    categoriesById?: MapById<CacCategory>,
    categoriesByCategoryId?: MapById<CacCategory & MetaProduct>,
    gamesById?: MapById<CacGame>,
    ingredientsById?: MapById<CacIngredient>,
    menusById?: MapById<CacMenu>,
    menusByCategoryId?: MapListById<CacMenu & MetaProduct>,
    operationsRepeat?: Array<OperationDevice>,
    productGroupsById?: MapById<CacProductGroup>,
    productGroupsByCategoryId?: MapListById<CacProductGroup & MetaProduct>,
    productsById?: MapById<CacProduct>,
    productsByCategoryId?: MapListById<CacProduct & MetaProduct>,
    promotionsById?: MapById<CacPromotion>,
    subCategoriesById?: MapById<CacCategory>,
    subCategoriesId?: Array<string>
}

const initialState: State = {
    selectedAllergens: [],
    withoutBacon: false
}

export const reducer = (state: State = initialState, action: Action): State => {
    switch (action.type) {
        case 'ECOMM_WITHOUT_BACON_TOGGLED':
            return {
                ...state,
                withoutBacon: !state.withoutBacon
            }
        case 'ECOMM_ALLERGENS_SELECTED':
            return {
                ...state,
                selectedAllergens: action.selectedAllergens
            }
        case 'ECOMM_CATALOG_FETCHED':
            const { catalog } = action
            const {
                allergens = [],
                categories = [],
                ingredients = [],
                menus = [],
                productGroups = [],
                products = [],
                subCategories = [],
                promotions = [],
                games = []
            } = catalog
            return {
                ...state,
                catalog,
                allergensById: mapById(allergens),
                categoriesById: mapById(categories),
                categoriesByCategoryId: mapByCategoryId(categories, 'CATEGORY'),
                ingredientsById: mapById(ingredients),
                menusById: mapById(menus),
                menusByCategoryId: mapByCategoryId(menus, 'MENU'),
                productGroupsById: mapById(productGroups),
                productGroupsByCategoryId: mapByCategoryId(
                    productGroups,
                    'PRODUCT_GROUP'
                ),
                productsById: mapById(products),
                productsByCategoryId: mapByCategoryId(products, 'PRODUCT'),
                subCategoriesById: mapById(subCategories),
                subCategoriesId: subCategories.map(
                    (subCategory: CacCategory) => subCategory.id
                ),
                promotionsById: mapById(promotions),
                gamesById: mapById(games)
            }
        case 'ECOMM_GET_OPERATIONS_REPEAT':
        case 'OPERATION_REPEAT_SWAP_CODE':
        case 'OPERATION_REPEAT_DRIVE_CODE':
            return { ...state, operationsRepeat: action.operationsRepeat }
        default:
            return state
    }
}

const mapById = (collection: Array<any>) =>
    collection.reduce((acc, item) => {
        const { id = null } = item
        if (id) {
            acc[id] = item
        }
        return acc
    }, {})

const mapByCategoryId = (
    collection: Array<any>,
    type: 'CATEGORY' | 'MENU' | 'PRODUCT' | 'PRODUCT_GROUP'
) =>
    collection.reduce((acc, item) => {
        const { categories = [] } = item
        if (categories) {
            categories.forEach((category: CacProductCategory) => {
                const categoryId = '' + category.id
                if (!acc.hasOwnProperty(categoryId)) {
                    acc[categoryId] = []
                }
                acc[categoryId].push({
                    ...item,
                    categoryId,
                    type,
                    displayOrder: category.displayOrder
                })
            })
        }
        return acc
    }, {})
